import { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { FaAngleLeft, FaAngleRight, FaEdit } from "react-icons/fa";
import { PAGE_SIZE } from "../Utils/constant";
import TextInput from "../common/TextInput";
import { supabase } from "../../supabaseClient";

const ScheduleTransportationTypesNew = () => {
  const [page, setPage] = useState(1);
  const [isEdit, setIsEdit] = useState(false);
  const [transportType, setTransportType] = useState(null);
  const [search, setSearch] = useState(null);
  const [transportTypesData, setTransportTypesData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [companies, setCompanies] = useState([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      CompanyID: "",
      Code: "",
      Description: "",
    },
  });

  useEffect(() => {
    if (transportType) {
      reset({
        CompanyID: transportType.CompanyID,
        Code: transportType.Code,
        Description: transportType.Description,
      });
    }
  }, [transportType, reset]);

  useEffect(() => {
    const fetchCompanies = async () => {
      const { data: Companies, error: cError } = await supabase
        .from("Companies")
        .select("CompanyID, Name");
      if (cError) {
        setCompanies([]);
      } else {
        setCompanies(Companies);
        reset((prev) => ({
          ...prev,
          CompanyID: Companies[0] ? Companies[0].CompanyID : "",
        }));
      }
    };
    fetchCompanies();
  }, [reset]);

  useEffect(() => {
    const fetchTransportTypes = async () => {
      let query = supabase
        .from("ScheduleTransportationTypes")
        .select("TransportationTypeID, CompanyID, Code, Description", {
          count: "exact",
        })
        .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);
      if (search && search.text) {
        query = query.ilike(search.type, `%${search.text}%`);
      }
      const { data, count, error } = await query;
      if (!error) {
        setTransportTypesData(data);
        setTotalCount(count);
      }
    };
    fetchTransportTypes();
  }, [page, search, isEdit]);

  const onNextPage = useCallback(() => {
    setPage((old) => old + 1);
  }, []);
  const onPrevPage = useCallback(() => {
    setPage((old) => Math.max(old - 1, 1));
  }, []);

  const onSuccess = useCallback(() => {
    toast.success("Success");
    setIsEdit(false);
    setTransportType(null);
  }, []);
  const onError = useCallback((err) => {
    toast.error("Error: " + err.message);
  }, []);

  const onAddNew = () => {
    reset({
      CompanyID: companies[0] ? companies[0].CompanyID : "",
      Code: "",
      Description: "",
    });
    setIsEdit(true);
    setTransportType(null);
  };

  const OnCancel = () => {
    setIsEdit(false);
    setTransportType(null);
  };

  const OnCreate = useCallback(
    async (data) => {
      if (transportType) {
        // Update
        const { error } = await supabase
          .from("ScheduleTransportationTypes")
          .update({
            CompanyID: data.CompanyID,
            Code: data.Code,
            Description: data.Description,
          })
          .eq("TransportationTypeID", transportType.TransportationTypeID);
        if (error) onError(error);
        else onSuccess();
      } else {
        // Create
        const { error } = await supabase
          .from("ScheduleTransportationTypes")
          .insert({
            CompanyID: data.CompanyID,
            Code: data.Code,
            Description: data.Description,
          });
        if (error) onError(error);
        else onSuccess();
      }
    },
    [transportType, onError, onSuccess]
  );

  const OnSearch = useCallback((data) => {
    setSearch(data);
  }, []);

  return (
    <>
      {isEdit ? (
        <div className="driver-patent-edit-content mt-4 rounded-lg">
          <div className="flex justify-between mb-2">
            <div>Company</div>
            <div className="w-35">
              <Controller
                control={control}
                name="CompanyID"
                rules={{ required: "Required" }}
                render={({ field: { value, onChange } }) => (
                  <select
                    className="custom-input rounded-lg"
                    value={value}
                    onChange={onChange}
                    error={errors.CompanyID?.message}
                  >
                    {companies.map((company) => (
                      <option key={company.CompanyID} value={company.CompanyID}>
                        {company.Name}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>
              <p>Code</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="Code"
                rules={{ required: "Code is required" }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.Code?.message}
                  />
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>
              <p>Description</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="Description"
                rules={{ required: "Description is required" }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.Description?.message}
                  />
                )}
              />
            </div>
          </div>
          <div className="flex gap-4 mt-4 save-button">
            <button
              disabled={false}
              onClick={handleSubmit(OnCreate)}
              className="justify-between p-2 pr-20 pl-20 rounded-lg mr-1 flex items-center custom-blue-button"
            >
              <span>{transportType ? "Update" : "Create"}</span>
            </button>
            <div>
              <div
                onClick={OnCancel}
                className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
              >
                <span>Cancel</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex ">
            <h5 className="mb-0">Schedule Transportation Types</h5>
            <button
              className="justify-between rounded-lg ml-1 flex items-center custom-blue-button border-none"
              onClick={onAddNew}
            >
              <span>Add New</span>
            </button>
          </div>
          <SearchBar setSearch={setSearch} OnSearch={OnSearch} />
          <table className="table-auto w-full mt-4 border border-gray-200">
            <thead>
              <tr className="bg-gray-100 cursor-pointer">
                <th className="px-4 py-2 border">ID</th>
                <th className="px-4 py-2 border">Company Name</th>
                <th className="px-4 py-2 border">Code</th>
                <th className="px-4 py-2 border">Description</th>
                <th className="px-4 py-2 border">Edit</th>
              </tr>
            </thead>
            <tbody>
              {transportTypesData &&
                transportTypesData.map((type) => (
                  <tr
                    key={type.TransportationTypeID}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-4 py-2 border">
                      {type.TransportationTypeID}
                    </td>
                    <td className="px-4 py-2 border">
                      {companies.find((c) => c.CompanyID === type.CompanyID)
                        ?.Name || ""}
                    </td>
                    <td className="px-4 py-2 border">{type.Code}</td>
                    <td className="px-4 py-2 border">{type.Description}</td>
                    <td className="px-4 py-2 border cursor-point">
                      <FaEdit
                        onClick={() => {
                          setTransportType(type);
                          setIsEdit(true);
                        }}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          <div className="pagination-container">
            <div className="pagination-content">
              <div>
                <p className="pagination-text">
                  Showing <span>{(page - 1) * PAGE_SIZE + 1}</span> to{" "}
                  <span>{Math.min(totalCount, page * PAGE_SIZE)}</span> of{" "}
                  <span>{totalCount}</span> results
                </p>
              </div>
              <div>
                <nav className="pagination-nav">
                  <button
                    onClick={onPrevPage}
                    disabled={page == 1}
                    className={`pagination-item ${page == 1 ? "disabled" : ""}`}
                  >
                    <FaAngleLeft className="pagination-icon" />
                  </button>
                  <button
                    onClick={onNextPage}
                    disabled={page * PAGE_SIZE > totalCount}
                    className={`pagination-item ${
                      page * PAGE_SIZE > totalCount ? "disabled" : ""
                    }`}
                  >
                    <FaAngleRight className="pagination-icon" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ScheduleTransportationTypesNew;

const SearchBar = ({ setSearch, OnSearch }) => {
  const {
    control: searchControl,
    handleSubmit: handleSearchSubmit,
    reset: searchReset,
  } = useForm({
    defaultValues: {
      type: "Code",
      text: "",
    },
  });

  return (
    <div className="flex mt-4 items-center">
      <div>
        <p className="mb-0">Look For:</p>
      </div>
      <div className="flex justify-between w-10 ml-1">
        <Controller
          control={searchControl}
          name="type"
          render={({ field: { value, onChange } }) => (
            <select
              className="custom-input rounded-lg cursor-pointer"
              value={value}
              onChange={onChange}
            >
              <option value={"Code"}>Code</option>
              <option value={"Description"}>Description</option>
            </select>
          )}
        />
      </div>
      <div className="ml-1">
        <Controller
          control={searchControl}
          name="text"
          rules={{ required: "Required" }}
          render={({ field: { value, onChange } }) => (
            <input
              type="text"
              value={value}
              onChange={onChange}
              className="custom-input rounded-lg w-100 "
            />
          )}
        />
      </div>
      <div className="ml-1 flex">
        <div>
          <button
            onClick={handleSearchSubmit(OnSearch)}
            className="justify-between rounded-lg flex items-center w-30 custom-search-button border-none"
          >
            <span>Search</span>
          </button>
        </div>
        <div>
          <button
            onClick={() => {
              searchReset({ type: "Code", text: "" });
              setSearch(null);
            }}
            className="justify-between rounded-lg ml-1 flex items-center custom-reset-button"
          >
            <span>Reset</span>
          </button>
        </div>
      </div>
    </div>
  );
};
